import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, validerDemande, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId?: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormAustralie: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const { title, prixconsulat, prixintervention, url_flag, validite, informations, demandeId } = state;
  const isEditMode = demandeId != null;

  // États issus des paramètres ou initialisés par défaut
  const [titreVisa] = useState<string>(title || '');
  const [prixConsulat] = useState<number>(prixconsulat || 0);
  const [prixIntervention] = useState<number>(prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(url_flag || '');
  const [validiteVisa] = useState<string>(validite || '');
  const commentaires = state?.commentaires || '';
  const [informationsVisa] = useState<string>(informations || '');

  // États du formulaire
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);

  // Champs spécifiques à ce formulaire (basés sur FormAustralie en natif)
  const [previousVisa, setPreviousVisa] = useState<string>(''); // "oui" ou "non"
  const [visaNumber, setVisaNumber] = useState<string>('');
  const [casierVierge, setCasierVierge] = useState<string>(''); // Doit être "non"

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [hideFees] = useState<boolean>(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Calcul du total (frais de visa + frais de service + supplément assurance)
  const total = prixConsulat + prixIntervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Formatage de la date en jj/mm/aaaa
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Fonction de validation du formulaire
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);
    const errors: string[] = [];

    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('australie.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('australie.errors.departureDateRequired'));
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('australie.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('australie.errors.returnDateRequired'));
    }

    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(tp('australie.validation.departureDateShort'));
    if (!dateSortie.trim()) errors.push(tp('australie.validation.returnDateShort'));
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    if (previousVisa === 'oui' && !visaNumber.trim()) {
      errors.push(tp('australie.validation.visaNumber'));
    }
    if (casierVierge !== 'oui') {
      errors.push(tp('australie.validation.casierVierge'));
    }

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  // Validation à chaque modification des champs
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateDepart, dateSortie, assuranceVisa, sliderValue, selectedVoyageur, previousVisa, visaNumber, casierVierge]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true);
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Gestion de la sortie de la page pour prévenir l'utilisateur
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleBack = () => {
    if (window.confirm(t('common.confirmation.unsavedChanges'))) {
      navigate(-1);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = champsNonRemplis.length === 0;
      const data: any = {
        etatFormulaire,
        idUtilisateur: userId,
        idVoyageur: selectedVoyageur,
        titreVisa: titreVisa,
        dateDepart,
        dateSortie,
        previousVisa,
        visaNumber: previousVisa === 'oui' ? visaNumber : '',
        casierVierge,
        assuranceVisa,
        prixconsulat: prixConsulat,
        prixintervention: prixIntervention,
        validite: validiteVisa,
      };

      const response = await validerDemande(data);
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.idDemande) {
        setCreatedDemandeId(response.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error("Erreur lors de la validation de la demande:", error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setPreviousVisa('');
    setVisaNumber('');
    setCasierVierge('');
  };

  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validiteVisa}
              <br />
              {informationsVisa}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(voyageur => (
                    <MenuItem key={voyageur.id} value={voyageur.id.toString()}>
                      {voyageur.nom} {voyageur.prenom}
                      {voyageur.numeropax ? t('common.selectTravelerWithPassport', { passport: voyageur.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Section visa antérieur */}
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="previous-visa-select-label">{tp('australie.questions.previousVisa')}</InputLabel>
            <Select
              labelId="previous-visa-select-label"
              id="previous-visa-select"
              value={previousVisa}
              label={tp('australie.questions.previousVisa')}
              onChange={(e: SelectChangeEvent) => setPreviousVisa(e.target.value)}
            >
              <MenuItem value="non">{t('common.no')}</MenuItem>
              <MenuItem value="oui">{t('common.yes')}</MenuItem>
            </Select>
          </FormControl>

          {previousVisa === 'oui' && (
            <TextField
              label={tp('australie.fields.visaNumber')}
              placeholder={tp('australie.fields.visaNumber')}
              value={visaNumber}
              onChange={(e) => setVisaNumber(e.target.value)}
              fullWidth
              margin="normal"
            />
          )}

          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="casier-select-label">{tp('australie.questions.casierVierge')}</InputLabel>
            <Select
              labelId="casier-select-label"
              id="casier-select"
              value={casierVierge}
              label={tp('australie.questions.casierVierge')}
              onChange={(e: SelectChangeEvent) => setCasierVierge(e.target.value)}
            >
              <MenuItem value="oui">{t('common.yes')}</MenuItem>
            </Select>
          </FormControl>

          {/* Assurance Visa identique à FormInde */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Typography variant="body1">{t('common.fees.visa', { prix: prixConsulat })}</Typography>
            <Typography variant="body1">{t('common.fees.service', { prix: prixIntervention })}</Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {t('common.fees.total', { total })}
            </Typography>
          </Box>

          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormAustralie;
